import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  Calculator,
  ChevronLeft,
  Clock3,
  Copy,
  Download,
  ImageIcon,
  Menu,
  PanelLeft,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Sparkles,
  StickyNote,
  Trash2,
  Type,
  Upload,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { getActiveAccountId } from '@auw/api-client';
import { activeTenantKey as tenantKey, activeTenantNamespacedKey as tenantNamespacedKey } from '@auw/tenant';
import {
  StorageContainer,
  SlideTrack,
  MainPane,
  ToolbarWrap,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbSep,
} from '@auw/workspace-ui';
import { Button } from '@auw/workspace-ui';
import { SelectDropdown, type SelectOption } from '@auw/workspace-ui';
import { DropOverlay } from '@auw/workspace-ui';
import { onAppEvent } from '@auw/stores';
import {
  ToolsSidebar,
  type ToolCategory,
  type ToolCategoryId,
  type ToolId,
} from './ToolsSidebar';

const TOOL_SELECTED_KEY = tenantNamespacedKey('tools', 'selected-tool');
const TOOL_OPEN_CATEGORIES_KEY = tenantNamespacedKey('tools', 'open-categories');
const TOOL_SIDEBAR_OPEN_KEY = tenantNamespacedKey('tools', 'sidebar-open');
const TOOL_MEMO_KEY = tenantNamespacedKey('tools', 'memo');
const TOOL_CHECKLIST_KEY = tenantNamespacedKey('tools', 'checklist');
const TOOL_FAVORITES_STORAGE_KEY = tenantKey('tools_favorite_ids');

function getToolFavoritesStorageKey(scope: string): string {
  return `${TOOL_FAVORITES_STORAGE_KEY}:${scope || 'anonymous'}`;
}

function readToolFavoriteIds(scope: string): string[] {
  try {
    const raw = localStorage.getItem(getToolFavoritesStorageKey(scope));
    return raw ? (JSON.parse(raw) as string[]).filter((v) => typeof v === 'string' && v.trim()) : [];
  } catch {
    return [];
  }
}

function writeToolFavoriteIds(scope: string, ids: string[]): void {
  try {
    localStorage.setItem(getToolFavoritesStorageKey(scope), JSON.stringify(ids));
  } catch { /* quota exceeded — ignore */ }
}

const CATEGORY_DEFS: ReadonlyArray<{
  id: ToolCategoryId;
  icon: typeof Calculator;
  toolIds: readonly ToolId[];
}> = [
  { id: 'calculation', icon: Calculator, toolIds: ['calculator', 'unit-converter', 'percentage'] },
  { id: 'time', icon: Clock3, toolIds: ['date-diff', 'd-day', 'timer-stopwatch'] },
  { id: 'productivity', icon: StickyNote, toolIds: ['memo', 'checklist'] },
  { id: 'text', icon: Type, toolIds: ['text-counter', 'text-transform'] },
  { id: 'developer', icon: Wrench, toolIds: ['json-formatter', 'uuid-generator', 'timestamp-converter'] },
  { id: 'file-image', icon: ImageIcon, toolIds: ['image-info', 'image-resizer'] },
  { id: 'random', icon: Sparkles, toolIds: ['random-picker', 'coin-dice'] },
];

const TOOL_IDS: ToolId[] = CATEGORY_DEFS.flatMap((c) => [...c.toolIds]);
const CATEGORY_IDS: ToolCategoryId[] = CATEGORY_DEFS.map((c) => c.id);
const DEFAULT_TOOL_ID: ToolId = 'calculator';
const DAY_MS = 24 * 60 * 60 * 1000;
const UUID_MAX_COUNT = 100;
const DICE_MIN_SIDES = 2;
const DICE_MAX_SIDES = 6;

const LENGTH_UNITS = [
  { value: 'mm', factor: 0.001 },
  { value: 'cm', factor: 0.01 },
  { value: 'm', factor: 1 },
  { value: 'km', factor: 1000 },
  { value: 'in', factor: 0.0254 },
  { value: 'ft', factor: 0.3048 },
] as const;

const MASS_UNITS = [
  { value: 'g', factor: 1 },
  { value: 'kg', factor: 1000 },
  { value: 'lb', factor: 453.59237 },
  { value: 'oz', factor: 28.349523125 },
] as const;

const TEMPERATURE_UNITS = [
  { value: 'c' },
  { value: 'f' },
  { value: 'k' },
] as const;

const TEMP_UNIT_KEYS: Record<string, string> = { c: 'celsius', f: 'fahrenheit', k: 'kelvin' };

const UNIT_DEFAULTS: Record<UnitGroup, { from: string; to: string }> = {
  length: { from: 'm', to: 'ft' },
  mass: { from: 'kg', to: 'lb' },
  temperature: { from: 'c', to: 'f' },
};

// UNIT_GROUP_OPTIONS moved inside component for i18n

const IMAGE_RESIZE_FORMAT_OPTIONS: SelectOption[] = [
  { value: 'image/png', label: 'PNG' },
  { value: 'image/jpeg', label: 'JPEG' },
  { value: 'image/webp', label: 'WEBP' },
];

// ASPECT_RATIO_OPTIONS moved inside component for i18n

const IMAGE_FILE_NAME_PATTERN = /\.(avif|bmp|gif|heic|heif|ico|jpe?g|png|svg|webp)$/i;

type UnitGroup = 'length' | 'mass' | 'temperature';
type ImageResizeFormat = 'image/png' | 'image/jpeg' | 'image/webp';

interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

interface ImageAsset {
  fileName: string;
  fileSize: number;
  mimeType: string;
  width: number;
  height: number;
  sourceUrl: string;
}

interface ResizedImage {
  url: string;
  fileName: string;
  fileSize: number;
  width: number;
  height: number;
  mimeType: ImageResizeFormat;
}

function readStoredValue<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeStoredValue<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 로컬 스토리지 접근 실패는 무시
  }
}

function isToolId(value: unknown): value is ToolId {
  return typeof value === 'string' && TOOL_IDS.includes(value as ToolId);
}

function isToolCategoryId(value: unknown): value is ToolCategoryId {
  return typeof value === 'string' && CATEGORY_IDS.includes(value as ToolCategoryId);
}

function parseNumberInput(value: string): number | null {
  if (!value.trim()) return null;
  const normalized = Number(value.replace(/,/g, '').trim());
  return Number.isFinite(normalized) ? normalized : null;
}

function parsePositiveInteger(value: string): number | null {
  const parsed = parseNumberInput(value);
  if (parsed === null) return null;
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
}

function formatNumber(value: number, maxFractionDigits = 4): string {
  if (!Number.isFinite(value)) return '-';
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
}

function formatByteSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${formatNumber(bytes / 1024, 2)} KB`;
  if (bytes < 1024 ** 3) return `${formatNumber(bytes / 1024 ** 2, 2)} MB`;
  return `${formatNumber(bytes / 1024 ** 3, 2)} GB`;
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatStopwatch(ms: number): string {
  const totalHundredths = Math.floor(ms / 10);
  const hours = Math.floor(totalHundredths / 360000);
  const minutes = Math.floor((totalHundredths % 360000) / 6000);
  const seconds = Math.floor((totalHundredths % 6000) / 100);
  const hundredths = totalHundredths % 100;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
}

function parseDateOnly(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getDayDiff(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / DAY_MS);
}

// 엄격한 CSP(`script-src` 에 'unsafe-eval' 미포함, 온프레미스/엔터프라이즈 빌드)에서는
// Function()/eval 이 차단되므로 직접 작성한 파서로 사칙연산을 평가한다.
// 문법: expr := term (('+'|'-') term)*, term := factor (('*'|'/') factor)*,
//       factor := unary ('%')* (후위 %는 100으로 나눔), unary := ('+'|'-') unary | primary,
//       primary := number | '(' expr ')'
function evaluateArithmetic(input: string): number {
  const tokens: string[] = [];
  let cursor = 0;

  while (cursor < input.length) {
    const char = input[cursor];
    if (char === ' ') {
      cursor += 1;
      continue;
    }
    if ((char >= '0' && char <= '9') || char === '.') {
      let number = '';
      while (
        cursor < input.length &&
        ((input[cursor] >= '0' && input[cursor] <= '9') || input[cursor] === '.')
      ) {
        number += input[cursor];
        cursor += 1;
      }
      tokens.push(number);
      continue;
    }
    if (char === '+' || char === '-' || char === '*' || char === '/' || char === '(' || char === ')' || char === '%') {
      tokens.push(char);
      cursor += 1;
      continue;
    }
    throw new Error('errors.invalidCharacters');
  }

  let pos = 0;
  const peek = (): string | undefined => tokens[pos];

  const parsePrimary = (): number => {
    const token = peek();
    if (token === '(') {
      pos += 1;
      const value = parseExpr();
      if (peek() !== ')') throw new Error('errors.invalidResult');
      pos += 1;
      return value;
    }
    if (token === undefined || !/^(?:\d+\.?\d*|\.\d+)$/.test(token)) {
      throw new Error('errors.invalidResult');
    }
    pos += 1;
    const value = Number(token);
    if (!Number.isFinite(value)) throw new Error('errors.invalidResult');
    return value;
  };

  const parseUnary = (): number => {
    if (peek() === '+') {
      pos += 1;
      return parseUnary();
    }
    if (peek() === '-') {
      pos += 1;
      return -parseUnary();
    }
    return parsePrimary();
  };

  const parseFactor = (): number => {
    let value = parseUnary();
    while (peek() === '%') {
      pos += 1;
      value /= 100;
    }
    return value;
  };

  const parseTerm = (): number => {
    let value = parseFactor();
    while (peek() === '*' || peek() === '/') {
      const op = tokens[pos];
      pos += 1;
      const rhs = parseFactor();
      value = op === '*' ? value * rhs : value / rhs;
    }
    return value;
  };

  function parseExpr(): number {
    let value = parseTerm();
    while (peek() === '+' || peek() === '-') {
      const op = tokens[pos];
      pos += 1;
      const rhs = parseTerm();
      value = op === '+' ? value + rhs : value - rhs;
    }
    return value;
  }

  const result = parseExpr();
  if (pos !== tokens.length) {
    throw new Error('errors.invalidResult');
  }
  return result;
}

function safeEvaluateExpression(expression: string): number {
  const normalized = expression
    .replace(/×/g, '*')
    .replace(/÷/g, '/')
    .replace(/,/g, '')
    .replace(/\s+/g, ' ');

  if (!normalized.trim()) {
    throw new Error('errors.emptyExpression');
  }

  if (!/^[0-9+\-*/().%\s]+$/.test(normalized)) {
    throw new Error('errors.invalidCharacters');
  }

  const result = evaluateArithmetic(normalized);

  if (typeof result !== 'number' || !Number.isFinite(result)) {
    throw new Error('errors.invalidResult');
  }

  return result;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) {
    const nonTextTypes = new Set([
      'button',
      'checkbox',
      'color',
      'file',
      'hidden',
      'image',
      'radio',
      'range',
      'reset',
      'submit',
    ]);
    return !nonTextTypes.has(target.type);
  }
  return target.isContentEditable;
}

function toTitleCase(value: string): string {
  return value.replace(/\w\S*/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
  );
}

function dedupeLines(value: string): string {
  const seen = new Set<string>();
  return value
    .split('\n')
    .filter((line) => {
      const key = line.trim();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join('\n');
}

function sortLines(value: string): string {
  return value
    .split('\n')
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .join('\n');
}

interface IntlSegmentData {
  segment: string;
  index: number;
  isWordLike?: boolean;
}

interface IntlSegmentsResult {
  [Symbol.iterator](): IterableIterator<IntlSegmentData>;
}

interface IntlSegmenterInstance {
  segment(input: string): IntlSegmentsResult;
}

type IntlSegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity?: 'grapheme' | 'word' | 'sentence' },
) => IntlSegmenterInstance;

// 띄어쓰기 기준 분리는 중국어/일본어처럼 공백이 없는 언어를 1단어로 잘못 센다.
// Intl.Segmenter(word)로 다국어 단어 분절을 수행하고, 미지원 환경에서는
// CJK 문자를 개별 단어로 세는 정규식 폴백을 사용한다.
function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;

  const SegmenterCtor = (Intl as unknown as { Segmenter?: IntlSegmenterConstructor }).Segmenter;
  if (typeof SegmenterCtor === 'function') {
    try {
      const segmenter = new SegmenterCtor(undefined, { granularity: 'word' });
      let count = 0;
      for (const part of segmenter.segment(trimmed)) {
        if (part.isWordLike) count += 1;
      }
      return count;
    } catch {
      // Intl.Segmenter 사용 불가 시 아래 폴백으로 진행
    }
  }

  const cjkPattern = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/g;
  const cjkMatches = trimmed.match(cjkPattern);
  const cjkCount = cjkMatches ? cjkMatches.length : 0;
  const nonCjk = trimmed.replace(cjkPattern, ' ').trim();
  const wordTokens = nonCjk ? nonCjk.split(/\s+/).filter(Boolean).length : 0;
  return cjkCount + wordTokens;
}

function makeChecklistItem(text: string): ChecklistItem {
  return {
    id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `check-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    done: false,
  };
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('errors.cannotLoadImage'));
    image.src = url;
  });
}

function convertTemperature(value: number, from: string, to: string): number {
  let celsius = value;

  if (from === 'f') celsius = (value - 32) * (5 / 9);
  if (from === 'k') celsius = value - 273.15;

  if (to === 'c') return celsius;
  if (to === 'f') return (celsius * 9) / 5 + 32;
  return celsius + 273.15;
}

function convertUnitValue(group: UnitGroup, value: number, from: string, to: string): number {
  if (group === 'temperature') {
    return convertTemperature(value, from, to);
  }

  const options = group === 'length' ? LENGTH_UNITS : MASS_UNITS;
  const fromOption = options.find((option) => option.value === from);
  const toOption = options.find((option) => option.value === to);
  if (!fromOption || !toOption) {
    return value;
  }
  const baseValue = value * fromOption.factor;
  return baseValue / toOption.factor;
}

function formatTimestampInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function formatDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shiftDateInput(value: string, days: number): string {
  const base = parseDateOnly(value) ?? new Date();
  const next = new Date(base);
  next.setDate(next.getDate() + days);
  return formatDateInput(next);
}

function parseTimestampToMs(value: string): number | null {
  const parsed = parseNumberInput(value);
  if (parsed === null) return null;
  return parsed > 1e12 ? parsed : parsed * 1000;
}

function downloadFromUrl(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  anchor.click();
}

const PageWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 0;
`;

const MainSidebarExpandBtn = styled.button`
  position: absolute;
  top: 8px;
  left: 16px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const CollapsedSidebarToggleSlot = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const CollapsedSidebarToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(33,33,33,0.72)' : 'rgba(255,255,255,0.76)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 18px rgba(0,0,0,0.24)'
      : '0 8px 18px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(58,58,58,0.86)' : 'rgba(255,255,255,0.92)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const MobileBackBtn = styled.button`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
    background: transparent;
    color: ${({ theme }) => theme.colors.textSecondary};
    cursor: pointer;

    &:hover {
      background: ${({ theme }) => theme.colors.bgHover};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  }
`;

const ToolScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 16px;
  }
`;

const PanelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ToolCard = styled.section`
  border-radius: 16px;
  padding: 20px;
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(24,24,26,0.84)'
      : 'rgba(255,255,255,0.82)'};
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 12px 32px rgba(0,0,0,0.24)'
      : '0 12px 32px rgba(0,0,0,0.05)'};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 16px;
  }
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CardDescription = styled.p`
  margin: 6px 0 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CardDivider = styled.div`
  height: 1px;
  margin: 18px 0;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.08)'
      : 'rgba(0,0,0,0.06)'};
`;

const FieldGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 2 }) => $columns}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const FieldHint = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const sharedInputStyle = css`
  width: 100%;
  min-height: 44px;
  border-radius: 10px;
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(0,0,0,0.03)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 12px 13px;
  font-size: 0.9375rem;
  outline: none;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 2px
      ${({ theme }) =>
        theme.mode === 'dark'
          ? 'rgba(255,149,0,0.18)'
          : 'rgba(0,0,0,0.06)'};
  }
`;

const TextInput = styled.input`
  ${sharedInputStyle}
`;

const SelectInput = styled(SelectDropdown)`
  width: 100%;

  button[aria-haspopup='listbox'] {
    min-height: 44px;
    padding: 0 13px;
    font-size: 0.9375rem;
  }

  [role='listbox'] {
    margin-top: 6px;
  }
`;

const UpwardSelectInput = styled(SelectInput)`
  [data-placement='top'][role='listbox'] {
    margin-top: 0;
  }
`;

const TextArea = styled.textarea`
  ${sharedInputStyle}
  min-height: 140px;
  resize: vertical;
  line-height: 1.6;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadPanel = styled.div<{ $hasAsset?: boolean; $dragActive?: boolean }>`
  position: relative;
  overflow: hidden;
  margin-top: 14px;
  padding: 18px;
  border-radius: 16px;
  border: 0.5px dashed
    ${({ $dragActive, $hasAsset, theme }) =>
      $dragActive
        ? (theme.mode === 'dark' ? 'rgba(255,149,0,0.4)' : 'rgba(255,149,0,0.28)')
        : $hasAsset
        ? (theme.mode === 'dark' ? 'rgba(255,149,0,0.24)' : 'rgba(255,149,0,0.2)')
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')};
  background: ${({ $dragActive, $hasAsset, theme }) =>
    $dragActive
      ? (theme.mode === 'dark' ? 'rgba(255,149,0,0.12)' : 'rgba(255,149,0,0.08)')
      : $hasAsset
      ? (theme.mode === 'dark' ? 'rgba(255,149,0,0.08)' : 'rgba(255,149,0,0.05)')
      : (theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')};
  transition: border-color ${({ theme }) => theme.transitions.fast},
    background ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};
  box-shadow: ${({ $dragActive }) =>
    $dragActive ? 'inset 0 0 0 1px rgba(255,149,0,0.18)' : 'none'};
`;

const UploadPanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const UploadPanelIcon = styled.div<{ $hasAsset?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.accent};
  background: ${({ $hasAsset, theme }) =>
    $hasAsset
      ? (theme.mode === 'dark' ? 'rgba(255,149,0,0.18)' : 'rgba(255,149,0,0.14)')
      : (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.75)')};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
      : 'inset 0 1px 0 rgba(255,255,255,0.8)'};
`;

const UploadPanelText = styled.div`
  flex: 1;
  min-width: 0;
`;

const UploadPanelTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const UploadPanelBody = styled.p`
  margin: 6px 0 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const UploadMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const UploadMetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.82)'};
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
`;

const UploadActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  min-height: 40px;
  padding: 0 15px;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: transform 0.15s ease, opacity 0.15s ease, background 0.15s ease;

  ${({ $variant = 'secondary', theme }) => {
    if ($variant === 'primary') {
      return css`
        background: ${theme.colors.accent};
        color: #fff;
      `;
    }
    if ($variant === 'danger') {
      return css`
        background: ${theme.colors.error};
        color: #fff;
      `;
    }
    return css`
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
      color: ${theme.colors.textPrimary};
    `;
  }}

  &:hover {
    transform: translateY(-1px);
    opacity: 0.95;
  }
`;

const IconActionButton = styled(ActionButton)`
  min-width: 40px;
  padding: 0 12px;
`;

const ResultBanner = styled.div<{ $tone?: 'info' | 'success' | 'error' }>`
  margin-top: 14px;
  padding: 13px 15px;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.5;
  border: 0.5px solid transparent;

  ${({ $tone = 'info', theme }) => {
    if ($tone === 'success') {
      return css`
        color: ${theme.colors.textPrimary};
        background: ${theme.mode === 'dark' ? 'rgba(255,149,0,0.14)' : 'rgba(255,149,0,0.1)'};
        border-color: ${theme.mode === 'dark' ? 'rgba(255,149,0,0.28)' : 'rgba(255,149,0,0.2)'};
      `;
    }
    if ($tone === 'error') {
      return css`
        color: ${theme.colors.textPrimary};
        background: ${theme.mode === 'dark' ? 'rgba(248,113,113,0.12)' : 'rgba(239,68,68,0.12)'};
        border-color: ${theme.mode === 'dark' ? 'rgba(248,113,113,0.24)' : 'rgba(239,68,68,0.24)'};
      `;
    }
    return css`
      color: ${theme.colors.textPrimary};
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
      border-color: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    `;
  }}
`;

const StatGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(${({ $columns = 3 }) => Math.max(1, Math.min($columns, 2))}, minmax(0, 1fr));
  }
`;

const StatCard = styled.div`
  border-radius: 12px;
  padding: 14px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.04)'
      : 'rgba(0,0,0,0.03)'};
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;
`;

const InlineMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const QuickActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const QuickActionChip = styled.button`
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.08)'
      : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(0,0,0,0.08)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ChecklistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
`;

const ChecklistRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.04)'
      : 'rgba(0,0,0,0.03)'};
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const CheckButton = styled.button<{ $done?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: none;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  background: ${({ $done, theme }) =>
    $done ? theme.colors.accent : theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'};
`;

const ChecklistText = styled.span<{ $done?: boolean }>`
  flex: 1;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
  opacity: ${({ $done }) => ($done ? 0.6 : 1)};
`;

const EmptyState = styled.div`
  padding: 18px 14px;
  border-radius: 12px;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.04)'
      : 'rgba(0,0,0,0.03)'};
  border: 0.5px dashed
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
`;

const PreviewCard = styled.div`
  margin-top: 14px;
  border-radius: 14px;
  overflow: hidden;
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : 'rgba(255,255,255,0.72)'};
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  background:
    linear-gradient(45deg, rgba(127,127,127,0.08) 25%, transparent 25%) -8px 0 / 16px 16px,
    linear-gradient(-45deg, rgba(127,127,127,0.08) 25%, transparent 25%) -8px 0 / 16px 16px,
    linear-gradient(45deg, transparent 75%, rgba(127,127,127,0.08) 75%) -8px 0 / 16px 16px,
    linear-gradient(-45deg, transparent 75%, rgba(127,127,127,0.08) 75%) -8px 0 / 16px 16px;
`;

const PreviewMeta = styled.div`
  padding: 12px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CalculatorShell = styled.div`
  margin-top: 14px;
  padding: 16px;
  border-radius: 16px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,246,248,0.94) 100%)'};
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
      : 'inset 0 1px 0 rgba(255,255,255,0.7)'};
`;

const CalculatorDisplay = styled.div`
  min-height: 122px;
  border-radius: 14px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(10,10,12,0.6)'
      : 'rgba(247,247,249,0.95)'};
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
`;

const CalculatorExpressionInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 1.125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  outline: none;
  text-align: right;
`;

const CalculatorResultText = styled.div`
  text-align: right;
  font-size: clamp(1.75rem, 3vw, 2.4rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-all;
`;

const CalculatorErrorText = styled.div`
  text-align: right;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.error};
  min-height: 18px;
`;

const CalculatorKeypad = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
`;

const CalculatorKey = styled.button<{
  $variant?: 'number' | 'operator' | 'action' | 'primary';
  $span?: number;
}>`
  min-height: 54px;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  grid-column: span ${({ $span = 1 }) => $span};
  transition: transform 0.15s ease, filter 0.15s ease, background 0.15s ease;

  ${({ $variant = 'number', theme }) => {
    if ($variant === 'primary') {
      return css`
        background: ${theme.colors.accent};
        color: #fff;
      `;
    }
    if ($variant === 'action') {
      return css`
        background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
        color: ${theme.colors.textPrimary};
      `;
    }
    if ($variant === 'operator') {
      return css`
        background: ${theme.mode === 'dark' ? 'rgba(255,149,0,0.16)' : 'rgba(255,149,0,0.14)'};
        color: ${theme.colors.textPrimary};
      `;
    }
    return css`
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.94)'};
      color: ${theme.colors.textPrimary};
      box-shadow: ${theme.mode === 'dark'
        ? '0 1px 0 rgba(255,255,255,0.03) inset'
        : '0 1px 0 rgba(255,255,255,0.8) inset'};
    `;
  }}

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }
`;

function kebabToCamel(str: string): string {
  return str.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}

export const ToolsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const mainSidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setMainSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const addToast = useChatStore((s) => s.addToast);
  const calculatorInputRef = useRef<HTMLInputElement | null>(null);

  const user = useUserStore((s) => s.user);
  const updateToolsFavoriteIds = useUserStore((s) => s.updateToolsFavoriteIds);

  const favoriteStorageScope = useMemo(
    () => getActiveAccountId() ?? user?.userId ?? 'anonymous',
    [user?.userId],
  );

  const toolCategories = useMemo<ToolCategory[]>(() =>
    CATEGORY_DEFS.map((def) => {
      const catKey = kebabToCamel(def.id);
      return {
        id: def.id,
        label: t(`tools:categories.${catKey}.label`),
        description: t(`tools:categories.${catKey}.description`),
        icon: def.icon,
        tools: def.toolIds.map((toolId) => {
          const toolKey = kebabToCamel(toolId);
          return {
            id: toolId,
            label: t(`tools:toolItems.${toolKey}.label`),
            shortLabel: t(`tools:toolItems.${toolKey}.shortLabel`),
            description: t(`tools:toolItems.${toolKey}.description`),
          };
        }),
      };
    }),
  [t]);

  const unitGroupOptions = useMemo<SelectOption[]>(() => [
    { value: 'length', label: t('tools:unitGroups.length') },
    { value: 'mass', label: t('tools:unitGroups.mass') },
    { value: 'temperature', label: t('tools:unitGroups.temperature') },
  ], [t]);

  const aspectRatioOptions = useMemo<SelectOption[]>(() => [
    { value: 'locked', label: t('tools:aspectRatio.locked') },
    { value: 'free', label: t('tools:aspectRatio.free') },
  ], [t]);

  const favoriteIdsRef = useRef<string[]>([]);
  const [favoriteToolIds, setFavoriteToolIds] = useState<string[]>(() =>
    readToolFavoriteIds('anonymous'),
  );
  const migratedFavoriteScopesRef = useRef(new Set<string>());

  useEffect(() => {
    const cachedIds = readToolFavoriteIds(favoriteStorageScope);

    if (!user?.userId) {
      favoriteIdsRef.current = cachedIds;
      setFavoriteToolIds(cachedIds);
      return;
    }

    const serverIds = Array.isArray(user.toolsFavoriteIds) ? user.toolsFavoriteIds : [];
    if (serverIds.length > 0 || cachedIds.length === 0) {
      favoriteIdsRef.current = serverIds;
      setFavoriteToolIds(serverIds);
      writeToolFavoriteIds(favoriteStorageScope, serverIds);
      return;
    }

    favoriteIdsRef.current = cachedIds;
    setFavoriteToolIds(cachedIds);
    writeToolFavoriteIds(favoriteStorageScope, cachedIds);

    if (migratedFavoriteScopesRef.current.has(favoriteStorageScope)) return;
    migratedFavoriteScopesRef.current.add(favoriteStorageScope);

    void updateToolsFavoriteIds(cachedIds).catch(() => {});
  }, [favoriteStorageScope, updateToolsFavoriteIds, user?.toolsFavoriteIds, user?.userId]);

  const toggleToolFavorite = useCallback((toolId: ToolId) => {
    const prev = favoriteIdsRef.current;
    const next = prev.includes(toolId)
      ? prev.filter((id) => id !== toolId)
      : [...prev, toolId];

    favoriteIdsRef.current = next;
    setFavoriteToolIds(next);
    writeToolFavoriteIds(favoriteStorageScope, next);

    if (!user?.userId) return;

    void updateToolsFavoriteIds(next).catch(() => {
      favoriteIdsRef.current = prev;
      setFavoriteToolIds(prev);
      writeToolFavoriteIds(favoriteStorageScope, prev);
      addToast({
        type: 'error',
        title: t('tools:errors.favoriteSaveFailed'),
        description: t('tools:errors.tryAgainLater'),
      });
    });
  }, [addToast, favoriteStorageScope, updateToolsFavoriteIds, user?.userId]);

  const [pageSidebarOpen, setPageSidebarOpen] = useState(() =>
    readStoredValue<boolean>(TOOL_SIDEBAR_OPEN_KEY, true),
  );
  const [mobileShowContent, setMobileShowContent] = useState(false);
  const togglePageSidebar = useCallback(() => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      setMobileShowContent((prev) => !prev);
      return;
    }
    setPageSidebarOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    return onAppEvent('workspace:togglePageSidebar', () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        setMobileShowContent((prev) => !prev);
        return;
      }
      setPageSidebarOpen((prev) => !prev);
    });
  }, []);

  useEffect(() => {
    writeStoredValue(TOOL_SIDEBAR_OPEN_KEY, pageSidebarOpen);
  }, [pageSidebarOpen]);

  const [selectedToolId, setSelectedToolId] = useState<ToolId>(() => {
    const stored = readStoredValue<string | null>(TOOL_SELECTED_KEY, null);
    return isToolId(stored) ? stored : DEFAULT_TOOL_ID;
  });

  const [openCategoryIds, setOpenCategoryIds] = useState<Set<ToolCategoryId>>(() => {
    const raw = window.localStorage.getItem(TOOL_OPEN_CATEGORIES_KEY);
    if (raw === null) return new Set(CATEGORY_IDS);
    const stored = readStoredValue<unknown[]>(TOOL_OPEN_CATEGORIES_KEY, CATEGORY_IDS);
    if (!Array.isArray(stored)) return new Set(CATEGORY_IDS);
    return new Set(stored.filter(isToolCategoryId));
  });

  useEffect(() => {
    writeStoredValue(TOOL_SELECTED_KEY, selectedToolId);
  }, [selectedToolId]);

  useEffect(() => {
    writeStoredValue(TOOL_OPEN_CATEGORIES_KEY, [...openCategoryIds]);
  }, [openCategoryIds]);

  const selectedCategory = useMemo(
    () => toolCategories.find((category) => category.tools.some((tool) => tool.id === selectedToolId)) ?? toolCategories[0],
    [selectedToolId, toolCategories],
  );

  const selectedTool = useMemo(
    () => selectedCategory.tools.find((tool) => tool.id === selectedToolId) ?? selectedCategory.tools[0],
    [selectedCategory, selectedToolId],
  );

  const ensureCategoryOpen = useCallback((categoryId: ToolCategoryId) => {
    setOpenCategoryIds((prev) => {
      if (prev.has(categoryId)) return prev;
      const next = new Set(prev);
      next.add(categoryId);
      return next;
    });
  }, []);

  const handleToggleCategory = useCallback((categoryId: ToolCategoryId) => {
    setOpenCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }, []);

  const handleSelectTool = useCallback((toolId: ToolId) => {
    setSelectedToolId(toolId);
    const category = toolCategories.find((item) => item.tools.some((tool) => tool.id === toolId));
    if (category) ensureCategoryOpen(category.id);
    setMobileShowContent(true);
  }, [ensureCategoryOpen, toolCategories]);

  const handleMobileBack = useCallback(() => {
    setMobileShowContent(false);
  }, []);

  const copyText = useCallback(async (text: string, successTitle: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast({ type: 'success', title: successTitle });
    } catch {
      addToast({ type: 'error', title: t('tools:errors.clipboardCopyFailed') });
    }
  }, [addToast]);

  const [calculatorExpression, setCalculatorExpression] = useState('');
  const [calculatorReplaceOnInput, setCalculatorReplaceOnInput] = useState(false);
  const [calculatorLastResolvedValue, setCalculatorLastResolvedValue] = useState('0');
  const [calculatorSubmittedError, setCalculatorSubmittedError] = useState<string | null>(null);

  useEffect(() => {
    if (!calculatorExpression.trim()) {
      setCalculatorLastResolvedValue('0');
      return;
    }

    try {
      const result = safeEvaluateExpression(calculatorExpression);
      setCalculatorLastResolvedValue(formatNumber(result, 6));
    } catch {
      // 입력 중 미완성 수식은 마지막 정상 결과를 유지
    }
  }, [calculatorExpression]);

  const appendCalculatorToken = useCallback((token: string) => {
    setCalculatorExpression((prev) => {
      const nextBase = (() => {
        if (!calculatorReplaceOnInput) return prev;
        if (/^[0-9.]$/.test(token) || token === '00' || token === '(') return '';
        return prev;
      })();

      const trimmedBase = nextBase.replace(/\s+$/, '');
      const lastChar = trimmedBase.slice(-1);
      const nextToken = token === '×' ? '*' : token === '÷' ? '/' : token;
      const operators = ['+', '-', '*', '/'];

      if (!trimmedBase && operators.includes(nextToken) && nextToken !== '-') {
        return nextBase;
      }

      if (nextToken === '%') {
        if (!/\d|\)/.test(lastChar)) return nextBase;
        return `${nextBase}%`;
      }

      if (operators.includes(nextToken) && operators.includes(lastChar)) {
        return `${trimmedBase.slice(0, -1)}${nextToken}`;
      }

      if (nextToken === '.' && /\.\d*$/.test(trimmedBase.split(/[-+*/()]/).pop() ?? '')) {
        return nextBase;
      }

      return `${nextBase}${nextToken}`;
    });
    setCalculatorReplaceOnInput(false);
    setCalculatorSubmittedError(null);
  }, [calculatorReplaceOnInput]);

  const handleCalculatorClear = useCallback(() => {
    setCalculatorExpression('');
    setCalculatorReplaceOnInput(false);
    setCalculatorLastResolvedValue('0');
    setCalculatorSubmittedError(null);
  }, []);

  const handleCalculatorBackspace = useCallback(() => {
    setCalculatorExpression((prev) => prev.slice(0, -1));
    setCalculatorReplaceOnInput(false);
    setCalculatorSubmittedError(null);
  }, []);

  const handleCalculatorEvaluate = useCallback(() => {
    try {
      if (!calculatorExpression.trim()) {
        setCalculatorLastResolvedValue('0');
        setCalculatorSubmittedError(null);
        return;
      }
      const result = safeEvaluateExpression(calculatorExpression);
      setCalculatorExpression(String(result));
      setCalculatorLastResolvedValue(formatNumber(result, 6));
      setCalculatorReplaceOnInput(true);
      setCalculatorSubmittedError(null);
    } catch (error) {
      setCalculatorReplaceOnInput(false);
      const errKey = error instanceof Error && error.message.startsWith('errors.')
        ? error.message
        : 'errors.cannotCalculate';
      setCalculatorSubmittedError(t(`tools:${errKey}`));
    }
  }, [calculatorExpression]);

  useEffect(() => {
    if (selectedTool.id !== 'calculator') return;
    const rafId = window.requestAnimationFrame(() => {
      calculatorInputRef.current?.focus();
      calculatorInputRef.current?.setSelectionRange(
        calculatorInputRef.current.value.length,
        calculatorInputRef.current.value.length,
      );
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [selectedTool.id]);

  useEffect(() => {
    if (selectedTool.id !== 'calculator') return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target;
      const targetIsEditable = isEditableTarget(target);
      const targetIsCalculatorInput = target === calculatorInputRef.current;

      if (targetIsEditable && !targetIsCalculatorInput) return;

      if (event.key === 'Enter' || event.key === '=') {
        event.preventDefault();
        handleCalculatorEvaluate();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        handleCalculatorClear();
        return;
      }

      if (targetIsCalculatorInput) return;

      if (event.key === 'Backspace') {
        event.preventDefault();
        handleCalculatorBackspace();
        return;
      }

      const allowedPattern = /^[0-9+\-*/().%]$/;
      if (allowedPattern.test(event.key)) {
        event.preventDefault();
        appendCalculatorToken(event.key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    appendCalculatorToken,
    handleCalculatorBackspace,
    handleCalculatorClear,
    handleCalculatorEvaluate,
    selectedTool.id,
  ]);

  const [unitGroup, setUnitGroup] = useState<UnitGroup>('length');
  const [unitAmount, setUnitAmount] = useState('1');
  const [unitFrom, setUnitFrom] = useState<string>('m');
  const [unitTo, setUnitTo] = useState<string>('ft');

  const activeUnitOptions = useMemo<SelectOption[]>(
    () => (unitGroup === 'length'
      ? LENGTH_UNITS
      : unitGroup === 'mass'
        ? MASS_UNITS
        : TEMPERATURE_UNITS).map((option) => ({
      value: option.value,
      label: t(`tools:units.${unitGroup === 'temperature' ? TEMP_UNIT_KEYS[option.value] : option.value}`),
    })),
    [t, unitGroup],
  );

  useEffect(() => {
    const defaults = UNIT_DEFAULTS[unitGroup];
    setUnitFrom(defaults.from);
    setUnitTo(defaults.to);
  }, [unitGroup]);

  const convertedUnitValue = useMemo(() => {
    const value = parseNumberInput(unitAmount);
    if (value === null) return null;
    return convertUnitValue(unitGroup, value, unitFrom, unitTo);
  }, [unitAmount, unitGroup, unitFrom, unitTo]);

  const [percentageBase, setPercentageBase] = useState('480');
  const [percentageRate, setPercentageRate] = useState('25');
  const [ratioPart, setRatioPart] = useState('42');
  const [ratioWhole, setRatioWhole] = useState('120');
  const [changeBase, setChangeBase] = useState('159000');
  const [changeRate, setChangeRate] = useState('-10');

  const percentageOfValue = useMemo(() => {
    const base = parseNumberInput(percentageBase);
    const rate = parseNumberInput(percentageRate);
    if (base === null || rate === null) return null;
    return base * (rate / 100);
  }, [percentageBase, percentageRate]);

  const ratioPercentValue = useMemo(() => {
    const part = parseNumberInput(ratioPart);
    const whole = parseNumberInput(ratioWhole);
    if (part === null || whole === null || whole === 0) return null;
    return (part / whole) * 100;
  }, [ratioPart, ratioWhole]);

  const changedAmountValue = useMemo(() => {
    const base = parseNumberInput(changeBase);
    const rate = parseNumberInput(changeRate);
    if (base === null || rate === null) return null;
    return base * (1 + rate / 100);
  }, [changeBase, changeRate]);

  const todayInputValue = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const [dateDiffStart, setDateDiffStart] = useState(todayInputValue);
  const [dateDiffEnd, setDateDiffEnd] = useState(todayInputValue);
  const dateDiffValue = useMemo(() => {
    const start = parseDateOnly(dateDiffStart);
    const end = parseDateOnly(dateDiffEnd);
    if (!start || !end) return null;
    return getDayDiff(start, end);
  }, [dateDiffEnd, dateDiffStart]);

  const [ddayLabel, setDdayLabel] = useState(() => t('tools:ui.defaultDdayLabel'));
  const [ddayDate, setDdayDate] = useState(todayInputValue);
  const ddayValue = useMemo(() => {
    const target = parseDateOnly(ddayDate);
    const today = parseDateOnly(todayInputValue);
    if (!target || !today) return null;
    return getDayDiff(today, target);
  }, [ddayDate, todayInputValue]);

  const [timerMinutes, setTimerMinutes] = useState('25');
  const [timerSeconds, setTimerSeconds] = useState('0');
  const initialTimerMs = useMemo(() => {
    const minutes = parsePositiveInteger(timerMinutes) ?? 0;
    const seconds = parsePositiveInteger(timerSeconds) ?? 0;
    return Math.max(0, (minutes * 60 + seconds) * 1000);
  }, [timerMinutes, timerSeconds]);
  const [timerRemainingMs, setTimerRemainingMs] = useState(initialTimerMs);
  const [timerRunning, setTimerRunning] = useState(false);
  // 타이머가 시작/일시정지된 활성 세션 상태인지 추적한다.
  // 일시정지(또는 입력값 변경)는 timerRunning 을 false 로 만들지만,
  // 일시정지 상태에서는 남은 시간을 초기값으로 되돌리면 안 된다.
  const [timerStarted, setTimerStarted] = useState(false);
  const timerTargetRef = useRef<number | null>(null);
  const timerFinishedRef = useRef(false);

  useEffect(() => {
    if (timerRunning || timerStarted) return;
    setTimerRemainingMs(initialTimerMs);
  }, [initialTimerMs, timerRunning, timerStarted]);

  useEffect(() => {
    if (!timerRunning) return undefined;

    const tick = () => {
      if (!timerTargetRef.current) return;
      const nextRemaining = Math.max(0, timerTargetRef.current - Date.now());
      setTimerRemainingMs(nextRemaining);

      if (nextRemaining === 0 && !timerFinishedRef.current) {
        timerFinishedRef.current = true;
        timerTargetRef.current = null;
        setTimerRunning(false);
        addToast({ type: 'success', title: t('tools:toasts.timerFinished') });
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 200);
    return () => window.clearInterval(intervalId);
  }, [addToast, timerRunning]);

  const handleToggleTimer = useCallback(() => {
    if (timerRunning) {
      if (timerTargetRef.current) {
        setTimerRemainingMs(Math.max(0, timerTargetRef.current - Date.now()));
      }
      timerTargetRef.current = null;
      setTimerRunning(false);
      return;
    }

    const baseMs = timerRemainingMs > 0 ? timerRemainingMs : initialTimerMs;
    if (baseMs <= 0) {
      addToast({ type: 'error', title: t('tools:errors.enterTimerTime') });
      return;
    }

    timerFinishedRef.current = false;
    timerTargetRef.current = Date.now() + baseMs;
    setTimerStarted(true);
    setTimerRunning(true);
  }, [addToast, initialTimerMs, timerRemainingMs, timerRunning]);

  const handleResetTimer = useCallback(() => {
    timerTargetRef.current = null;
    timerFinishedRef.current = false;
    setTimerStarted(false);
    setTimerRunning(false);
    setTimerRemainingMs(initialTimerMs);
  }, [initialTimerMs]);

  const [stopwatchElapsedMs, setStopwatchElapsedMs] = useState(0);
  const [stopwatchRunning, setStopwatchRunning] = useState(false);
  const stopwatchStartedAtRef = useRef<number | null>(null);
  const stopwatchBaseRef = useRef(0);

  useEffect(() => {
    if (!stopwatchRunning) return undefined;
    const intervalId = window.setInterval(() => {
      if (!stopwatchStartedAtRef.current) return;
      setStopwatchElapsedMs(stopwatchBaseRef.current + (Date.now() - stopwatchStartedAtRef.current));
    }, 16);
    return () => window.clearInterval(intervalId);
  }, [stopwatchRunning]);

  const handleToggleStopwatch = useCallback(() => {
    if (stopwatchRunning) {
      stopwatchBaseRef.current = stopwatchElapsedMs;
      stopwatchStartedAtRef.current = null;
      setStopwatchRunning(false);
      return;
    }

    stopwatchBaseRef.current = stopwatchElapsedMs;
    stopwatchStartedAtRef.current = Date.now();
    setStopwatchRunning(true);
  }, [stopwatchElapsedMs, stopwatchRunning]);

  const handleResetStopwatch = useCallback(() => {
    stopwatchStartedAtRef.current = null;
    stopwatchBaseRef.current = 0;
    setStopwatchElapsedMs(0);
    setStopwatchRunning(false);
  }, []);

  const [memoText, setMemoText] = useState(() => readStoredValue<string>(TOOL_MEMO_KEY, ''));
  useEffect(() => {
    writeStoredValue(TOOL_MEMO_KEY, memoText);
  }, [memoText]);

  const [checklistDraft, setChecklistDraft] = useState('');
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(() => {
    const stored = readStoredValue<ChecklistItem[]>(TOOL_CHECKLIST_KEY, []);
    if (!Array.isArray(stored)) return [];
    return stored.filter((item) => (
      item
      && typeof item.id === 'string'
      && typeof item.text === 'string'
      && typeof item.done === 'boolean'
    ));
  });

  useEffect(() => {
    writeStoredValue(TOOL_CHECKLIST_KEY, checklistItems);
  }, [checklistItems]);

  const completedChecklistCount = checklistItems.filter((item) => item.done).length;

  const handleAddChecklistItem = useCallback(() => {
    const nextText = checklistDraft.trim();
    if (!nextText) return;
    setChecklistItems((prev) => [makeChecklistItem(nextText), ...prev]);
    setChecklistDraft('');
  }, [checklistDraft]);

  const [textCounterInput, setTextCounterInput] = useState('');
  const textMetrics = useMemo(() => {
    const characters = textCounterInput.length;
    const charactersWithoutSpaces = textCounterInput.replace(/\s/g, '').length;
    const words = countWords(textCounterInput);
    const lines = textCounterInput.length === 0 ? 0 : textCounterInput.split('\n').length;
    return { characters, charactersWithoutSpaces, words, lines };
  }, [textCounterInput]);

  const [textTransformInput, setTextTransformInput] = useState('');
  const [textTransformOutput, setTextTransformOutput] = useState('');

  const applyTextTransform = useCallback((transformer: (value: string) => string) => {
    setTextTransformOutput(transformer(textTransformInput));
  }, [textTransformInput]);

  const [jsonInput, setJsonInput] = useState('{\n  "message": "hello",\n  "count": 3\n}');
  const [jsonStatus, setJsonStatus] = useState<{ tone: 'info' | 'success' | 'error'; message: string } | null>(null);

  const handleFormatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonInput);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonInput(formatted);
      setJsonStatus({ tone: 'success', message: t('tools:toasts.jsonFormatted') });
    } catch (error) {
      setJsonStatus({
        tone: 'error',
        message: error instanceof Error ? error.message : t('tools:errors.jsonParseError'),
      });
    }
  }, [jsonInput]);

  const handleMinifyJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonInput);
      const minified = JSON.stringify(parsed);
      setJsonInput(minified);
      setJsonStatus({ tone: 'success', message: t('tools:toasts.jsonMinified') });
    } catch (error) {
      setJsonStatus({
        tone: 'error',
        message: error instanceof Error ? error.message : t('tools:errors.jsonParseError'),
      });
    }
  }, [jsonInput]);

  const handleValidateJson = useCallback(() => {
    try {
      JSON.parse(jsonInput);
      setJsonStatus({ tone: 'success', message: t('tools:toasts.jsonValid') });
    } catch (error) {
      setJsonStatus({
        tone: 'error',
        message: error instanceof Error ? error.message : t('tools:errors.jsonParseError'),
      });
    }
  }, [jsonInput]);

  const [uuidCount, setUuidCount] = useState('5');
  const [generatedUuids, setGeneratedUuids] = useState<string[]>(() =>
    Array.from({ length: 5 }, () => crypto.randomUUID()),
  );

  const handleGenerateUuids = useCallback(() => {
    const requested = parsePositiveInteger(uuidCount) ?? 1;
    const count = Math.min(UUID_MAX_COUNT, Math.max(1, requested));
    if (requested > UUID_MAX_COUNT) {
      addToast({
        type: 'info',
        title: t('tools:toasts.uuidMaxClamped', { max: UUID_MAX_COUNT }),
      });
      setUuidCount(String(UUID_MAX_COUNT));
    }
    setGeneratedUuids(Array.from({ length: count }, () => crypto.randomUUID()));
  }, [addToast, uuidCount]);

  const [timestampInput, setTimestampInput] = useState(() => `${Math.floor(Date.now() / 1000)}`);
  const [dateTimeInput, setDateTimeInput] = useState(() => formatTimestampInput(new Date()));
  const timestampMsValue = useMemo(() => parseTimestampToMs(timestampInput), [timestampInput]);
  const timestampDateValue = useMemo(
    () => (timestampMsValue === null ? null : new Date(timestampMsValue)),
    [timestampMsValue],
  );
  const dateTimeValue = useMemo(() => {
    if (!dateTimeInput) return null;
    const parsed = new Date(dateTimeInput);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [dateTimeInput]);

  const handleLoadNow = useCallback(() => {
    const now = new Date();
    setTimestampInput(`${Math.floor(now.getTime() / 1000)}`);
    setDateTimeInput(formatTimestampInput(now));
  }, []);

  const sourceImageUrlRef = useRef<string | null>(null);
  const resizedImageUrlRef = useRef<string | null>(null);
  const imageDropDepthRef = useRef(0);
  const imageFileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageAsset, setImageAsset] = useState<ImageAsset | null>(null);
  const [imageDropActive, setImageDropActive] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [resizeWidth, setResizeWidth] = useState('');
  const [resizeHeight, setResizeHeight] = useState('');
  const [keepAspectRatio, setKeepAspectRatio] = useState(true);
  const [resizeFormat, setResizeFormat] = useState<ImageResizeFormat>('image/png');
  const [resizedImage, setResizedImage] = useState<ResizedImage | null>(null);

  useEffect(() => () => {
    if (sourceImageUrlRef.current) {
      URL.revokeObjectURL(sourceImageUrlRef.current);
    }
    if (resizedImageUrlRef.current) {
      URL.revokeObjectURL(resizedImageUrlRef.current);
    }
  }, []);

  const handleImageFileSelect = useCallback(async (file: File) => {
    if (!(file.type.startsWith('image/') || IMAGE_FILE_NAME_PATTERN.test(file.name))) {
      setImageUploadError(t('tools:errors.imageOnlyUpload'));
      return;
    }

    try {
      if (sourceImageUrlRef.current) {
        URL.revokeObjectURL(sourceImageUrlRef.current);
      }
      if (resizedImageUrlRef.current) {
        URL.revokeObjectURL(resizedImageUrlRef.current);
        resizedImageUrlRef.current = null;
      }

      const sourceUrl = URL.createObjectURL(file);
      sourceImageUrlRef.current = sourceUrl;
      const image = await loadImageElement(sourceUrl);
      setImageAsset({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'image/*',
        width: image.width,
        height: image.height,
        sourceUrl,
      });
      setResizeWidth(String(image.width));
      setResizeHeight(String(image.height));
      setResizedImage(null);
      setImageUploadError(null);
    } catch (error) {
      const imgErr = error instanceof Error && error.message.startsWith('errors.')
        ? t(`tools:${error.message}`)
        : t('tools:errors.cannotLoadImage');
      setImageUploadError(imgErr);
    }
  }, []);

  const handleImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await handleImageFileSelect(file);
    } finally {
      event.target.value = '';
    }
  }, [handleImageFileSelect]);

  const handleResizeWidthChange = useCallback((value: string) => {
    setResizeWidth(value);
    if (!keepAspectRatio || !imageAsset) return;
    const width = parsePositiveInteger(value);
    if (width === null) return;
    setResizeHeight(String(Math.max(1, Math.round((width / imageAsset.width) * imageAsset.height))));
  }, [imageAsset, keepAspectRatio]);

  const handleResizeHeightChange = useCallback((value: string) => {
    setResizeHeight(value);
    if (!keepAspectRatio || !imageAsset) return;
    const height = parsePositiveInteger(value);
    if (height === null) return;
    setResizeWidth(String(Math.max(1, Math.round((height / imageAsset.height) * imageAsset.width))));
  }, [imageAsset, keepAspectRatio]);

  const handleGenerateResizedImage = useCallback(async () => {
    if (!imageAsset) {
      addToast({ type: 'error', title: t('tools:errors.uploadImageFirst') });
      return;
    }

    const width = parsePositiveInteger(resizeWidth);
    const height = parsePositiveInteger(resizeHeight);
    if (!width || !height) {
      addToast({ type: 'error', title: t('tools:errors.checkOutputSize') });
      return;
    }

    try {
      const image = await loadImageElement(imageAsset.sourceUrl);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error(t('tools:errors.cannotCreateCanvas'));
      }
      context.drawImage(image, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, resizeFormat, resizeFormat === 'image/jpeg' ? 0.92 : undefined);
      });

      if (!blob) {
        throw new Error(t('tools:errors.cannotGenerateOutput'));
      }

      if (resizedImageUrlRef.current) {
        URL.revokeObjectURL(resizedImageUrlRef.current);
      }

      const outputUrl = URL.createObjectURL(blob);
      resizedImageUrlRef.current = outputUrl;

      const fileExtension = resizeFormat === 'image/jpeg'
        ? 'jpg'
        : resizeFormat === 'image/webp'
          ? 'webp'
          : 'png';

      const baseName = imageAsset.fileName.replace(/\.[^.]+$/, '') || 'image';
      setResizedImage({
        url: outputUrl,
        fileName: `${baseName}-${width}x${height}.${fileExtension}`,
        fileSize: blob.size,
        width,
        height,
        mimeType: resizeFormat,
      });
      addToast({ type: 'success', title: t('tools:toasts.resizeComplete') });
    } catch (error) {
      addToast({
        type: 'error',
        title: error instanceof Error ? error.message : t('tools:errors.imageConvertFailed'),
      });
    }
  }, [addToast, imageAsset, resizeFormat, resizeHeight, resizeWidth]);

  const openImagePicker = useCallback(() => {
    imageFileInputRef.current?.click();
  }, []);

  const handleImageDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    imageDropDepthRef.current += 1;
    setImageDropActive(true);
  }, []);

  const handleImageDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setImageDropActive(true);
  }, []);

  const handleImageDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    imageDropDepthRef.current = Math.max(0, imageDropDepthRef.current - 1);
    if (imageDropDepthRef.current === 0) {
      setImageDropActive(false);
    }
  }, []);

  const handleImageDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    imageDropDepthRef.current = 0;
    setImageDropActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await handleImageFileSelect(file);
  }, [handleImageFileSelect]);

  const [randomOptionsInput, setRandomOptionsInput] = useState(
    () => [t('tools:defaults.randomOption1'), t('tools:defaults.randomOption2'), t('tools:defaults.randomOption3'), t('tools:defaults.randomOption4')].join('\n'),
  );
  const [randomSelection, setRandomSelection] = useState('');
  // 앞쪽 공백(쉼표 구분/들여쓰기에서 생기는)만 제거하고 뒤쪽 공백은 보존한다.
  // 사용자가 뒤 공백 개수로 항목을 구분(unique)하는 경우를 지원하기 위함.
  const randomOptions = useMemo(
    () => randomOptionsInput
      .split(/\n|,/)
      .map((item) => item.replace(/^\s+/, ''))
      .filter((item) => item.length > 0),
    [randomOptionsInput],
  );

  const handlePickRandomOption = useCallback(() => {
    if (randomOptions.length === 0) {
      addToast({ type: 'error', title: t('tools:errors.enterOneOrMoreOptions') });
      return;
    }
    const picked = randomOptions[Math.floor(Math.random() * randomOptions.length)];
    setRandomSelection(picked);
  }, [addToast, randomOptions]);

  const [coinResult, setCoinResult] = useState('');
  const [diceSides, setDiceSides] = useState('6');
  const [diceResult, setDiceResult] = useState<number | null>(null);

  const handleFlipCoin = useCallback(() => {
    setCoinResult(Math.random() > 0.5 ? t('tools:ui.heads') : t('tools:ui.tails'));
  }, []);

  const handleRollDice = useCallback(() => {
    const requested = parsePositiveInteger(diceSides) ?? 6;
    const sides = Math.min(DICE_MAX_SIDES, Math.max(DICE_MIN_SIDES, requested));
    if (requested > DICE_MAX_SIDES) {
      addToast({
        type: 'info',
        title: t('tools:toasts.diceMaxClamped', { max: DICE_MAX_SIDES }),
      });
      setDiceSides(String(DICE_MAX_SIDES));
    }
    setDiceResult(Math.floor(Math.random() * sides) + 1);
  }, [addToast, diceSides]);

  const renderImageUploadCard = () => (
    <ToolCard>
      <CardTitle>{t('tools:panels.imageUpload.title')}</CardTitle>
      <CardDescription>{t('tools:panels.imageUpload.description')}</CardDescription>
      <FileInput
        ref={imageFileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
      />
      <UploadPanel
        $hasAsset={!!imageAsset}
        $dragActive={imageDropActive}
        onDragEnter={handleImageDragEnter}
        onDragOver={handleImageDragOver}
        onDragLeave={handleImageDragLeave}
        onDrop={handleImageDrop}
      >
        {imageDropActive && (
          <DropOverlay label={t('tools:ui.dropImageHere')} />
        )}
        <UploadPanelHead>
          <UploadPanelIcon $hasAsset={!!imageAsset}>
            <ImageIcon size={22} />
          </UploadPanelIcon>
          <UploadPanelText>
            <UploadPanelTitle>
              {imageAsset ? t('tools:ui.imageReady') : t('tools:ui.selectImageToUpload')}
            </UploadPanelTitle>
            {imageAsset && (
              <UploadPanelBody>
                {t('tools:ui.imageUpdateNotice')}
              </UploadPanelBody>
            )}
            <UploadMetaRow>
              {imageAsset ? (
                <>
                  <UploadMetaChip>{imageAsset.fileName}</UploadMetaChip>
                  <UploadMetaChip>{imageAsset.width} x {imageAsset.height}</UploadMetaChip>
                  <UploadMetaChip>{formatByteSize(imageAsset.fileSize)}</UploadMetaChip>
                  <UploadMetaChip>{imageAsset.mimeType}</UploadMetaChip>
                </>
              ) : (
                <>
                  <UploadMetaChip>PNG / JPG / WEBP</UploadMetaChip>
                  <UploadMetaChip>{t('tools:ui.instantPreview')}</UploadMetaChip>
                  <UploadMetaChip>{t('tools:ui.resizeReady')}</UploadMetaChip>
                </>
              )}
            </UploadMetaRow>
          </UploadPanelText>
        </UploadPanelHead>
        <UploadActionRow>
          <Button
            variant="primary"
            size="md"
            leftIcon={imageAsset ? <RotateCcw size={16} /> : <Upload size={16} />}
            onClick={openImagePicker}
          >
            {imageAsset ? t('tools:ui.selectAnotherImage') : t('tools:ui.selectImage')}
          </Button>
        </UploadActionRow>
      </UploadPanel>
      {imageUploadError && (
        <ResultBanner $tone="error">{imageUploadError}</ResultBanner>
      )}
      {imageAsset && (
        <PreviewCard>
          <PreviewImage src={imageAsset.sourceUrl} alt={imageAsset.fileName} />
          <PreviewMeta>
            <span>{imageAsset.fileName}</span>
            <span>{imageAsset.width} x {imageAsset.height}</span>
            <span>{formatByteSize(imageAsset.fileSize)}</span>
            <span>{imageAsset.mimeType}</span>
          </PreviewMeta>
        </PreviewCard>
      )}
    </ToolCard>
  );

  const renderSelectedTool = () => {
    switch (selectedTool.id) {
      case 'calculator':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.calculator.title')}</CardTitle>
              <CardDescription>{t('tools:panels.calculator.description')}</CardDescription>
              <CalculatorShell>
                <CalculatorDisplay>
                  <CalculatorExpressionInput
                    ref={calculatorInputRef}
                    value={calculatorExpression}
                    onChange={(event) => {
                      setCalculatorExpression(event.target.value);
                      setCalculatorReplaceOnInput(false);
                      setCalculatorSubmittedError(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleCalculatorEvaluate();
                      }
                    }}
                    placeholder="0"
                    inputMode="decimal"
                    aria-label={t('tools:ui.calculatorInput')}
                  />
                  <div>
                    <CalculatorResultText>
                      {calculatorSubmittedError ? t('tools:ui.error') : calculatorLastResolvedValue}
                    </CalculatorResultText>
                    <CalculatorErrorText>
                      {calculatorSubmittedError ?? ''}
                    </CalculatorErrorText>
                  </div>
                </CalculatorDisplay>

                <CalculatorKeypad>
                  <CalculatorKey type="button" $variant="action" onClick={handleCalculatorClear}>AC</CalculatorKey>
                  <CalculatorKey type="button" $variant="action" onClick={() => appendCalculatorToken('(')}>(</CalculatorKey>
                  <CalculatorKey type="button" $variant="action" onClick={() => appendCalculatorToken(')')}>)</CalculatorKey>
                  <CalculatorKey type="button" $variant="action" onClick={handleCalculatorBackspace}>⌫</CalculatorKey>

                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('7')}>7</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('8')}>8</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('9')}>9</CalculatorKey>
                  <CalculatorKey type="button" $variant="operator" onClick={() => appendCalculatorToken('/')}>÷</CalculatorKey>

                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('4')}>4</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('5')}>5</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('6')}>6</CalculatorKey>
                  <CalculatorKey type="button" $variant="operator" onClick={() => appendCalculatorToken('*')}>×</CalculatorKey>

                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('1')}>1</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('2')}>2</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('3')}>3</CalculatorKey>
                  <CalculatorKey type="button" $variant="operator" onClick={() => appendCalculatorToken('-')}>-</CalculatorKey>

                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('0')}>0</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('00')}>00</CalculatorKey>
                  <CalculatorKey type="button" onClick={() => appendCalculatorToken('.')}>.</CalculatorKey>
                  <CalculatorKey type="button" $variant="operator" onClick={() => appendCalculatorToken('+')}>+</CalculatorKey>

                  <CalculatorKey type="button" $variant="operator" $span={2} onClick={() => appendCalculatorToken('%')}>%</CalculatorKey>
                  <CalculatorKey type="button" $variant="operator" $span={2} onClick={handleCalculatorEvaluate}>=</CalculatorKey>
                </CalculatorKeypad>
              </CalculatorShell>
            </ToolCard>
          </PanelStack>
        );

      case 'unit-converter':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.unitConverter.title')}</CardTitle>
              <CardDescription>{t('tools:panels.unitConverter.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.category')}</FieldLabel>
                  <SelectInput
                    options={unitGroupOptions}
                    value={unitGroup}
                    onChange={(value) => setUnitGroup(value as UnitGroup)}
                  />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.value')}</FieldLabel>
                  <TextInput
                    value={unitAmount}
                    onChange={(event) => setUnitAmount(event.target.value)}
                    placeholder={t('tools:ui.enterNumber')}
                  />
                </Field>
              </FieldGrid>
              <FieldGrid style={{ marginTop: 12 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.sourceUnit')}</FieldLabel>
                  <SelectInput
                    options={activeUnitOptions}
                    value={unitFrom}
                    onChange={setUnitFrom}
                  />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.targetUnit')}</FieldLabel>
                  <SelectInput
                    options={activeUnitOptions}
                    value={unitTo}
                    onChange={setUnitTo}
                  />
                </Field>
              </FieldGrid>
              <ButtonRow>
                <ActionButton
                  onClick={() => {
                    setUnitFrom(unitTo);
                    setUnitTo(unitFrom);
                  }}
                >
                  {t('tools:ui.swapUnits')}
                </ActionButton>
              </ButtonRow>
              <ResultBanner $tone="success">
                {convertedUnitValue === null
                  ? t('tools:ui.enterNumberToConvert')
                  : `${formatNumber(parseNumberInput(unitAmount) ?? 0, 6)} ${unitFrom} = ${formatNumber(convertedUnitValue, 6)} ${unitTo}`}
              </ResultBanner>
            </ToolCard>
          </PanelStack>
        );

      case 'percentage':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.percentage.title')}</CardTitle>
              <CardDescription>{t('tools:panels.percentage.description')}</CardDescription>
              <CardDivider />
              <SectionTitle>{t('tools:ui.xPercentOfY')}</SectionTitle>
              <FieldGrid>
                <Field>
                  <FieldLabel>{t('tools:ui.baseValue')}</FieldLabel>
                  <TextInput value={percentageBase} onChange={(event) => setPercentageBase(event.target.value)} />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.percent')}</FieldLabel>
                  <TextInput value={percentageRate} onChange={(event) => setPercentageRate(event.target.value)} />
                </Field>
              </FieldGrid>
              <ResultBanner $tone="success">
                {percentageOfValue === null ? t('tools:ui.enterNumbers') : `${formatNumber(percentageOfValue, 4)}`}
              </ResultBanner>

              <CardDivider />
              <SectionTitle>{t('tools:ui.aIsWhatPercentOfB')}</SectionTitle>
              <FieldGrid>
                <Field>
                  <FieldLabel>A</FieldLabel>
                  <TextInput value={ratioPart} onChange={(event) => setRatioPart(event.target.value)} />
                </Field>
                <Field>
                  <FieldLabel>B</FieldLabel>
                  <TextInput value={ratioWhole} onChange={(event) => setRatioWhole(event.target.value)} />
                </Field>
              </FieldGrid>
              <ResultBanner $tone="success">
                {ratioPercentValue === null ? t('tools:ui.enterNumbers') : `${formatNumber(ratioPercentValue, 4)}%`}
              </ResultBanner>

              <CardDivider />
              <SectionTitle>{t('tools:ui.increaseDecrease')}</SectionTitle>
              <FieldGrid>
                <Field>
                  <FieldLabel>{t('tools:ui.baseAmount')}</FieldLabel>
                  <TextInput value={changeBase} onChange={(event) => setChangeBase(event.target.value)} />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.changeRate')}</FieldLabel>
                  <TextInput value={changeRate} onChange={(event) => setChangeRate(event.target.value)} />
                </Field>
              </FieldGrid>
              <ResultBanner $tone="success">
                {changedAmountValue === null ? t('tools:ui.enterNumbers') : `${formatNumber(changedAmountValue, 2)}`}
              </ResultBanner>
            </ToolCard>
          </PanelStack>
        );

      case 'date-diff':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.dateDiff.title')}</CardTitle>
              <CardDescription>{t('tools:panels.dateDiff.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.startDate')}</FieldLabel>
                  <TextInput type="date" value={dateDiffStart} onChange={(event) => setDateDiffStart(event.target.value)} />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.endDate')}</FieldLabel>
                  <TextInput type="date" value={dateDiffEnd} onChange={(event) => setDateDiffEnd(event.target.value)} />
                </Field>
              </FieldGrid>
              <QuickActionRow>
                <QuickActionChip type="button" onClick={() => setDateDiffStart(todayInputValue)}>{t('tools:ui.startDateToday')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => setDateDiffEnd(todayInputValue)}>{t('tools:ui.endDateToday')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => setDateDiffEnd(shiftDateInput(dateDiffStart, 7))}>{t('tools:ui.plus7days')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => setDateDiffEnd(shiftDateInput(dateDiffStart, 30))}>{t('tools:ui.plus30days')}</QuickActionChip>
              </QuickActionRow>
              {dateDiffValue !== null && (
                <StatGrid>
                  <StatCard>
                    <StatLabel>{t('tools:ui.dayDifference')}</StatLabel>
                    <StatValue>{formatNumber(dateDiffValue, 0)}{t('tools:ui.dayUnit')}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>{t('tools:ui.weekDifference')}</StatLabel>
                    <StatValue>{formatNumber(dateDiffValue / 7, 2)}{t('tools:ui.weekUnit')}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>{t('tools:ui.hourDifference')}</StatLabel>
                    <StatValue>{formatNumber(dateDiffValue * 24, 0)}{t('tools:ui.hourUnit')}</StatValue>
                  </StatCard>
                </StatGrid>
              )}
            </ToolCard>
          </PanelStack>
        );

      case 'd-day': {
        const ddayLabelText = ddayValue === null
          ? '-'
          : ddayValue === 0
            ? 'D-Day'
            : ddayValue > 0
              ? `D-${ddayValue}`
              : `D+${Math.abs(ddayValue)}`;

        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.dDay.title')}</CardTitle>
              <CardDescription>{t('tools:panels.dDay.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.name')}</FieldLabel>
                  <TextInput value={ddayLabel} onChange={(event) => setDdayLabel(event.target.value)} placeholder={t('tools:ui.ddayPlaceholder')} />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.referenceDate')}</FieldLabel>
                  <TextInput type="date" value={ddayDate} onChange={(event) => setDdayDate(event.target.value)} />
                </Field>
              </FieldGrid>
              <QuickActionRow>
                <QuickActionChip type="button" onClick={() => setDdayDate(todayInputValue)}>{t('tools:ui.today')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => setDdayDate(shiftDateInput(todayInputValue, 1))}>{t('tools:ui.tomorrow')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => setDdayDate(shiftDateInput(todayInputValue, 7))}>{t('tools:ui.in7days')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => setDdayDate(shiftDateInput(todayInputValue, 30))}>{t('tools:ui.in30days')}</QuickActionChip>
              </QuickActionRow>
              <ResultBanner $tone="success">
                {ddayLabel || t('tools:ui.schedule')}: <strong>{ddayLabelText}</strong>
              </ResultBanner>
            </ToolCard>
          </PanelStack>
        );
      }

      case 'timer-stopwatch':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.timer.title')}</CardTitle>
              <CardDescription>{t('tools:panels.timer.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.minutes')}</FieldLabel>
                  <TextInput value={timerMinutes} onChange={(event) => setTimerMinutes(event.target.value)} />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.seconds')}</FieldLabel>
                  <TextInput value={timerSeconds} onChange={(event) => setTimerSeconds(event.target.value)} />
                </Field>
              </FieldGrid>
              <QuickActionRow>
                <QuickActionChip type="button" onClick={() => { setTimerMinutes('5'); setTimerSeconds('0'); }}>{t('tools:ui.fiveMin')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => { setTimerMinutes('15'); setTimerSeconds('0'); }}>{t('tools:ui.fifteenMin')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => { setTimerMinutes('25'); setTimerSeconds('0'); }}>{t('tools:ui.twentyFiveMin')}</QuickActionChip>
                <QuickActionChip type="button" onClick={() => { setTimerMinutes('50'); setTimerSeconds('0'); }}>{t('tools:ui.fiftyMin')}</QuickActionChip>
              </QuickActionRow>
              <ResultBanner $tone="success">
                {t('tools:ui.remainingTime')} <strong>{formatCountdown(timerRemainingMs)}</strong>
              </ResultBanner>
              <ButtonRow>
                <ActionButton onClick={handleToggleTimer}>
                  {timerRunning ? <Pause size={16} /> : <Play size={16} />}
                  {timerRunning ? t('tools:ui.pause') : t('tools:ui.start')}
                </ActionButton>
                <ActionButton onClick={handleResetTimer}>
                  <RotateCcw size={16} />
                  {t('tools:ui.reset')}
                </ActionButton>
              </ButtonRow>
            </ToolCard>

            <ToolCard>
              <CardTitle>{t('tools:panels.stopwatch.title')}</CardTitle>
              <CardDescription>{t('tools:panels.stopwatch.description')}</CardDescription>
              <ResultBanner $tone="success">
                {t('tools:ui.elapsedTime')} <strong>{formatStopwatch(stopwatchElapsedMs)}</strong>
              </ResultBanner>
              <ButtonRow>
                <ActionButton onClick={handleToggleStopwatch}>
                  {stopwatchRunning ? <Pause size={16} /> : <Play size={16} />}
                  {stopwatchRunning ? t('tools:ui.pause') : t('tools:ui.start')}
                </ActionButton>
                <ActionButton onClick={handleResetStopwatch}>
                  <RotateCcw size={16} />
                  {t('tools:ui.reset')}
                </ActionButton>
              </ButtonRow>
            </ToolCard>
          </PanelStack>
        );

      case 'memo':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.memo.title')}</CardTitle>
              <CardDescription>{t('tools:panels.memo.description')}</CardDescription>
              <Field style={{ marginTop: 14 }}>
                <FieldLabel>{t('tools:ui.memo')}</FieldLabel>
                <TextArea
                  value={memoText}
                  onChange={(event) => setMemoText(event.target.value)}
                  placeholder={t('tools:ui.memoPlaceholder')}
                />
              </Field>
              <ButtonRow>
                <ActionButton onClick={() => setMemoText('')}>{t('tools:ui.clear')}</ActionButton>
                <ActionButton onClick={() => copyText(memoText, t('tools:toasts.memoCopied'))}>
                  <Copy size={16} />
                  {t('tools:ui.copy')}
                </ActionButton>
              </ButtonRow>
            </ToolCard>
          </PanelStack>
        );

      case 'checklist':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.checklist.title')}</CardTitle>
              <CardDescription>{t('tools:panels.checklist.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.newItem')}</FieldLabel>
                  <TextInput
                    value={checklistDraft}
                    onChange={(event) => setChecklistDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      // 한글 등 IME 조합 중 Enter 는 글자 확정용이므로 무시한다.
                      // (조합 중 추가하면 마지막 글자가 중복 입력되는 문제 방지)
                      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
                      event.preventDefault();
                      handleAddChecklistItem();
                    }}
                    placeholder={t('tools:ui.checklistPlaceholder')}
                  />
                </Field>
              </FieldGrid>
              <ButtonRow>
                <ActionButton onClick={handleAddChecklistItem}>
                  <Plus size={16} />
                  {t('tools:ui.addItem')}
                </ActionButton>
                <ActionButton onClick={() => setChecklistItems((prev) => prev.filter((item) => !item.done))}>
                  {t('tools:ui.deleteCompleted')}
                </ActionButton>
                <ActionButton onClick={() => setChecklistItems([])}>
                  {t('tools:ui.deleteAll')}
                </ActionButton>
              </ButtonRow>
              <ResultBanner $tone="info">
                {t('tools:ui.completedCount', { done: completedChecklistCount, total: checklistItems.length })}
              </ResultBanner>
              {checklistItems.length === 0 ? (
                <EmptyState>{t('tools:ui.emptyChecklist')}</EmptyState>
              ) : (
                <ChecklistList>
                  {checklistItems.map((item) => (
                    <ChecklistRow key={item.id}>
                      <CheckButton
                        type="button"
                        $done={item.done}
                        onClick={() => {
                          setChecklistItems((prev) =>
                            prev.map((current) =>
                              current.id === item.id ? { ...current, done: !current.done } : current,
                            ),
                          );
                        }}
                      >
                        {item.done && <span>✓</span>}
                      </CheckButton>
                      <ChecklistText $done={item.done}>{item.text}</ChecklistText>
                      <IconActionButton
                        type="button"
                        $variant="danger"
                        onClick={() => setChecklistItems((prev) => prev.filter((current) => current.id !== item.id))}
                        title={t('tools:ui.delete')}
                      >
                        <Trash2 size={14} />
                      </IconActionButton>
                    </ChecklistRow>
                  ))}
                </ChecklistList>
              )}
            </ToolCard>
          </PanelStack>
        );

      case 'text-counter':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.textCounter.title')}</CardTitle>
              <CardDescription>{t('tools:panels.textCounter.description')}</CardDescription>
              <Field style={{ marginTop: 14 }}>
                <FieldLabel>{t('tools:ui.text')}</FieldLabel>
                <TextArea
                  value={textCounterInput}
                  onChange={(event) => setTextCounterInput(event.target.value)}
                  placeholder={t('tools:ui.textCounterPlaceholder')}
                />
              </Field>
              <StatGrid>
                <StatCard>
                  <StatLabel>{t('tools:ui.charCount')}</StatLabel>
                  <StatValue>{formatNumber(textMetrics.characters, 0)}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>{t('tools:ui.charCountNoSpaces')}</StatLabel>
                  <StatValue>{formatNumber(textMetrics.charactersWithoutSpaces, 0)}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>{t('tools:ui.wordCount')}</StatLabel>
                  <StatValue>{formatNumber(textMetrics.words, 0)}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>{t('tools:ui.lineCount')}</StatLabel>
                  <StatValue>{formatNumber(textMetrics.lines, 0)}</StatValue>
                </StatCard>
              </StatGrid>
            </ToolCard>
          </PanelStack>
        );

      case 'text-transform':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.textTransform.title')}</CardTitle>
              <CardDescription>{t('tools:panels.textTransform.description')}</CardDescription>
              <Field style={{ marginTop: 14 }}>
                <FieldLabel>{t('tools:ui.input')}</FieldLabel>
                <TextArea
                  value={textTransformInput}
                  onChange={(event) => setTextTransformInput(event.target.value)}
                  placeholder={t('tools:ui.textTransformPlaceholder')}
                />
              </Field>
              <ButtonRow>
                <ActionButton onClick={() => applyTextTransform((value) => value.toUpperCase())}>UPPERCASE</ActionButton>
                <ActionButton onClick={() => applyTextTransform((value) => value.toLowerCase())}>lowercase</ActionButton>
                <ActionButton onClick={() => applyTextTransform(toTitleCase)}>Title Case</ActionButton>
                <ActionButton onClick={() => applyTextTransform(dedupeLines)}>{t('tools:ui.dedupeLines')}</ActionButton>
                <ActionButton onClick={() => applyTextTransform(sortLines)}>{t('tools:ui.sortLines')}</ActionButton>
                <ActionButton onClick={() => applyTextTransform((value) => value.trim())}>{t('tools:ui.trimWhitespace')}</ActionButton>
              </ButtonRow>
              <Field style={{ marginTop: 14 }}>
                <FieldLabel>{t('tools:ui.output')}</FieldLabel>
                <TextArea value={textTransformOutput} readOnly placeholder={t('tools:ui.textTransformOutputPlaceholder')} />
              </Field>
              <ButtonRow>
                <ActionButton onClick={() => copyText(textTransformOutput, t('tools:toasts.transformResultCopied'))}>
                  <Copy size={16} />
                  {t('tools:ui.copyResult')}
                </ActionButton>
                <ActionButton onClick={() => setTextTransformInput(textTransformOutput)}>{t('tools:ui.useAsInput')}</ActionButton>
              </ButtonRow>
            </ToolCard>
          </PanelStack>
        );

      case 'json-formatter':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.jsonFormatter.title')}</CardTitle>
              <CardDescription>{t('tools:panels.jsonFormatter.description')}</CardDescription>
              <Field style={{ marginTop: 14 }}>
                <FieldLabel>JSON</FieldLabel>
                <TextArea value={jsonInput} onChange={(event) => setJsonInput(event.target.value)} />
              </Field>
              <ButtonRow>
                <ActionButton onClick={handleFormatJson}>{t('tools:ui.format')}</ActionButton>
                <ActionButton onClick={handleMinifyJson}>{t('tools:ui.minify')}</ActionButton>
                <ActionButton onClick={handleValidateJson}>{t('tools:ui.validate')}</ActionButton>
                <ActionButton onClick={() => copyText(jsonInput, t('tools:toasts.jsonCopied'))}>
                  <Copy size={16} />
                  {t('tools:ui.copy')}
                </ActionButton>
              </ButtonRow>
              {jsonStatus && (
                <ResultBanner $tone={jsonStatus.tone}>{jsonStatus.message}</ResultBanner>
              )}
            </ToolCard>
          </PanelStack>
        );

      case 'uuid-generator':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.uuidGenerator.title')}</CardTitle>
              <CardDescription>{t('tools:panels.uuidGenerator.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.generateCount')}</FieldLabel>
                  <TextInput
                    type="number"
                    min={1}
                    max={UUID_MAX_COUNT}
                    value={uuidCount}
                    onChange={(event) => setUuidCount(event.target.value)}
                  />
                  <FieldHint>{t('tools:ui.uuidMaxHint', { max: UUID_MAX_COUNT })}</FieldHint>
                </Field>
              </FieldGrid>
              <ButtonRow>
                <ActionButton onClick={handleGenerateUuids}>{t('tools:ui.generateNew')}</ActionButton>
                <ActionButton onClick={() => copyText(generatedUuids.join('\n'), t('tools:toasts.uuidListCopied'))}>
                  <Copy size={16} />
                  {t('tools:ui.copyAll')}
                </ActionButton>
              </ButtonRow>
              <StatGrid $columns={1}>
                {generatedUuids.map((uuid) => (
                  <StatCard key={uuid}>
                    <StatLabel>UUID</StatLabel>
                    <StatValue>{uuid}</StatValue>
                  </StatCard>
                ))}
              </StatGrid>
            </ToolCard>
          </PanelStack>
        );

      case 'timestamp-converter':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.timestampConverter.title')}</CardTitle>
              <CardDescription>{t('tools:panels.timestampConverter.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.timestampLabel')}</FieldLabel>
                  <TextInput value={timestampInput} onChange={(event) => setTimestampInput(event.target.value)} />
                </Field>
                <Field>
                  <FieldLabel>{t('tools:ui.dateTime')}</FieldLabel>
                  <TextInput type="datetime-local" value={dateTimeInput} onChange={(event) => setDateTimeInput(event.target.value)} />
                </Field>
              </FieldGrid>
              <ButtonRow>
                <ActionButton onClick={handleLoadNow}>{t('tools:ui.loadCurrentTime')}</ActionButton>
                <ActionButton onClick={() => copyText(timestampInput, t('tools:toasts.timestampCopied'))}>
                  <Copy size={16} />
                  {t('tools:ui.copyTimestamp')}
                </ActionButton>
              </ButtonRow>
              <StatGrid>
                <StatCard>
                  <StatLabel>{t('tools:ui.timestampParsed')}</StatLabel>
                  <StatValue>{timestampDateValue ? timestampDateValue.toLocaleString() : '-'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>ISO</StatLabel>
                  <StatValue>{timestampDateValue ? timestampDateValue.toISOString() : '-'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>{t('tools:ui.dateToTimestampMs')}</StatLabel>
                  <StatValue>{dateTimeValue ? `${dateTimeValue.getTime()}` : '-'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>{t('tools:ui.dateToTimestampSec')}</StatLabel>
                  <StatValue>{dateTimeValue ? `${Math.floor(dateTimeValue.getTime() / 1000)}` : '-'}</StatValue>
                </StatCard>
              </StatGrid>
            </ToolCard>
          </PanelStack>
        );

      case 'image-info':
        return (
          <PanelStack>
            {renderImageUploadCard()}
            <ToolCard>
              <CardTitle>{t('tools:panels.imageInfo.title')}</CardTitle>
              <CardDescription style={{ marginBottom: 14 }}>{t('tools:panels.imageInfo.description')}</CardDescription>
              {!imageAsset ? (
                <EmptyState>{t('tools:ui.emptyImageInfo')}</EmptyState>
              ) : (
                <StatGrid>
                  <StatCard>
                    <StatLabel>{t('tools:ui.fileName')}</StatLabel>
                    <StatValue>{imageAsset.fileName}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>{t('tools:ui.resolution')}</StatLabel>
                    <StatValue>{imageAsset.width} x {imageAsset.height}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>{t('tools:ui.fileSize')}</StatLabel>
                    <StatValue>{formatByteSize(imageAsset.fileSize)}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>{t('tools:ui.fileFormat')}</StatLabel>
                    <StatValue>{imageAsset.mimeType}</StatValue>
                  </StatCard>
                </StatGrid>
              )}
            </ToolCard>
          </PanelStack>
        );

      case 'image-resizer':
        return (
          <PanelStack>
            {renderImageUploadCard()}
            <ToolCard>
              <CardTitle>{t('tools:panels.imageResizer.title')}</CardTitle>
              <CardDescription style={{ marginBottom: 14 }}>{t('tools:panels.imageResizer.description')}</CardDescription>
              {!imageAsset ? (
                <EmptyState>{t('tools:ui.uploadImageFirstEmpty')}</EmptyState>
              ) : (
                <>
                  <FieldGrid style={{ marginTop: 14 }}>
                    <Field>
                      <FieldLabel>{t('tools:ui.widthPx')}</FieldLabel>
                      <TextInput value={resizeWidth} onChange={(event) => handleResizeWidthChange(event.target.value)} />
                    </Field>
                    <Field>
                      <FieldLabel>{t('tools:ui.heightPx')}</FieldLabel>
                      <TextInput value={resizeHeight} onChange={(event) => handleResizeHeightChange(event.target.value)} />
                    </Field>
                  </FieldGrid>
                  <FieldGrid style={{ marginTop: 12 }}>
                    <Field>
                      <FieldLabel>{t('tools:ui.outputFormat')}</FieldLabel>
                      <UpwardSelectInput
                        options={IMAGE_RESIZE_FORMAT_OPTIONS}
                        value={resizeFormat}
                        placement="top"
                        onChange={(value) => setResizeFormat(value as ImageResizeFormat)}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>{t('tools:ui.aspectRatioLock')}</FieldLabel>
                      <UpwardSelectInput
                        options={aspectRatioOptions}
                        value={keepAspectRatio ? 'locked' : 'free'}
                        placement="top"
                        onChange={(value) => setKeepAspectRatio(value === 'locked')}
                      />
                    </Field>
                  </FieldGrid>
                  <ButtonRow>
                    <ActionButton onClick={handleGenerateResizedImage}>
                      {t('tools:ui.generateNewImage')}
                    </ActionButton>
                    {resizedImage && (
                      <ActionButton
                        onClick={() => downloadFromUrl(resizedImage.url, resizedImage.fileName)}
                      >
                        <Download size={16} />
                        {t('tools:ui.download')}
                      </ActionButton>
                    )}
                  </ButtonRow>
                  {resizedImage && (
                    <>
                      <PreviewCard>
                        <PreviewImage src={resizedImage.url} alt={resizedImage.fileName} />
                        <PreviewMeta>
                          <span>{resizedImage.fileName}</span>
                          <span>{resizedImage.width} x {resizedImage.height}</span>
                          <span>{formatByteSize(resizedImage.fileSize)}</span>
                          <span>{resizedImage.mimeType}</span>
                        </PreviewMeta>
                      </PreviewCard>
                    </>
                  )}
                </>
              )}
            </ToolCard>
          </PanelStack>
        );

      case 'random-picker':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.randomPicker.title')}</CardTitle>
              <CardDescription>{t('tools:panels.randomPicker.description')}</CardDescription>
              <Field style={{ marginTop: 14 }}>
                <FieldLabel>{t('tools:ui.optionList')}</FieldLabel>
                <TextArea
                  value={randomOptionsInput}
                  onChange={(event) => setRandomOptionsInput(event.target.value)}
                  placeholder={t('tools:ui.onePerLine')}
                />
              </Field>
              <QuickActionRow>
                <QuickActionChip
                  type="button"
                  onClick={() => setRandomOptionsInput([t('tools:defaults.randomOption1'), t('tools:defaults.randomOption2'), t('tools:defaults.randomOption3'), t('tools:defaults.randomOption4')].join('\n'))}
                >
                  {t('tools:ui.insertSample')}
                </QuickActionChip>
                <QuickActionChip type="button" onClick={() => setRandomOptionsInput('')}>{t('tools:ui.clear')}</QuickActionChip>
              </QuickActionRow>
              <ButtonRow>
                <ActionButton onClick={handlePickRandomOption}>{t('tools:ui.pickRandom')}</ActionButton>
              </ButtonRow>
              <ResultBanner $tone="success">
                {t('tools:ui.selectionResult')}{' '}
                <strong style={{ whiteSpace: 'pre-wrap' }}>{randomSelection || '-'}</strong>
              </ResultBanner>
              <InlineMeta>{t('tools:ui.currentOptionCount')} {randomOptions.length}</InlineMeta>
            </ToolCard>
          </PanelStack>
        );

      case 'coin-dice':
        return (
          <PanelStack>
            <ToolCard>
              <CardTitle>{t('tools:panels.coin.title')}</CardTitle>
              <CardDescription>{t('tools:panels.coin.description')}</CardDescription>
              <ButtonRow>
                <ActionButton onClick={handleFlipCoin}>{t('tools:ui.flipCoin')}</ActionButton>
              </ButtonRow>
              <ResultBanner $tone="success">
                {t('tools:ui.result')} <strong>{coinResult || '-'}</strong>
              </ResultBanner>
            </ToolCard>

            <ToolCard>
              <CardTitle>{t('tools:panels.dice.title')}</CardTitle>
              <CardDescription>{t('tools:panels.dice.description')}</CardDescription>
              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <FieldLabel>{t('tools:ui.diceSides')}</FieldLabel>
                  <TextInput
                    type="number"
                    min={DICE_MIN_SIDES}
                    max={DICE_MAX_SIDES}
                    value={diceSides}
                    onChange={(event) => setDiceSides(event.target.value)}
                  />
                  <FieldHint>{t('tools:ui.diceMaxHint', { min: DICE_MIN_SIDES, max: DICE_MAX_SIDES })}</FieldHint>
                </Field>
              </FieldGrid>
              <ButtonRow>
                <ActionButton onClick={handleRollDice}>{t('tools:ui.rollDice')}</ActionButton>
              </ButtonRow>
              <ResultBanner $tone="success">
                {t('tools:ui.result')} <strong>{diceResult ?? '-'}</strong>
              </ResultBanner>
            </ToolCard>
          </PanelStack>
        );

      default:
        return null;
    }
  };

  return (
    <PageWrap>
      {!mainSidebarOpen && (
        <MainSidebarExpandBtn
          type="button"
          onClick={() => setMainSidebarOpen(true)}
          title={t('tools:ui.sidebarOpen')}
        >
          <PanelLeft size={18} />
        </MainSidebarExpandBtn>
      )}

      <StorageContainer>
        <SlideTrack $showContent={mobileShowContent}>
          <ToolsSidebar
            categories={toolCategories}
            selectedToolId={selectedTool.id}
            openCategoryIds={openCategoryIds}
            onToggleCategory={handleToggleCategory}
            onSelectTool={handleSelectTool}
            pageSidebarOpen={pageSidebarOpen}
            onTogglePageSidebar={togglePageSidebar}
            desktopHidden={!pageSidebarOpen}
            favoriteToolIds={favoriteToolIds}
            onToggleFavorite={toggleToolFavorite}
          />

          <MainPane>
            {!pageSidebarOpen && (
              <CollapsedSidebarToggleSlot>
                <CollapsedSidebarToggleBtn
                  type="button"
                  onClick={togglePageSidebar}
                  title={t('tools:ui.sidebarExpand')}
                >
                  <Menu size={16} />
                </CollapsedSidebarToggleBtn>
              </CollapsedSidebarToggleSlot>
            )}

            <ToolbarWrap $sidebarCollapsed={!pageSidebarOpen}>
              <MobileBackBtn type="button" onClick={handleMobileBack} title={t('tools:ui.backToCategories')}>
                <ChevronLeft size={16} />
              </MobileBackBtn>
              <Breadcrumb>
                <BreadcrumbItem
                  type="button"
                  onClick={() => ensureCategoryOpen(selectedCategory.id)}
                >
                  {selectedCategory.label}
                </BreadcrumbItem>
                <BreadcrumbSep>/</BreadcrumbSep>
                <BreadcrumbItem type="button" $isLast>
                  {selectedTool.label}
                </BreadcrumbItem>
              </Breadcrumb>
            </ToolbarWrap>

            <ToolScroll>
              {renderSelectedTool()}
            </ToolScroll>
          </MainPane>
        </SlideTrack>
      </StorageContainer>
    </PageWrap>
  );
};

export default ToolsPage;
